import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { overlaps } from '@/src/renderer/lib/titleParser/boundaries'
import {
  carryRanges, parseTaskTitle, recognizableRanges, remapRanges, remapSegments,
  type KeptSegment, type ParsedTitle, type ParseTitleOptions, type TextRange, type TitleCatalog, type TitleToken,
} from '@/src/renderer/lib/titleParser'
import type { LifeSpace, Section, Tag } from '@/src/renderer/lib/db/models'
import type { TitleRecognitionPlace } from '../TitleRecognitionScope'

const EMPTY: ParsedTitle = { title: '', tokens: [], suppressed: [], fields: { tags: [] } }
const NO_CATALOG: TitleCatalog = { projects: [], sections: [], tags: [] }
const NO_RANGES: TextRange[] = []

interface UseTitleRecognitionArgs {
  /** Ohne Ort keine Erkennung (siehe `TitleRecognitionScope`). */
  place: TitleRecognitionPlace | null
  enabled: boolean
  title: string
  /** `i18n.language` — „de-DE“, „en“ … (vor dem Laden von i18n auch leer). */
  language: string | undefined
  /** Wechselt um Mitternacht, damit „morgen“ weiter morgen meint. */
  dateKey: unknown
  /** Die Tags, die die Zeile ohnehin liest. */
  tags: Tag[]
  /**
   * Beim Bearbeiten der gespeicherte Titel (Entscheidung 4): Was darin schon erkennbar war, gilt als
   * Text gelassen — markiert wird nur neu Getipptes. Ein Klick auf ein altes Wort markiert es trotzdem.
   */
  protectedText?: string | null
  /** Wechselt, wenn die Zeile auf- oder zuklappt oder geleert wird — dann beginnt alles von vorn. */
  resetSignal?: unknown
}

/** Wohin die Aufgabe laut Titel gehört — `#Projekt`, `/Abschnitt`. */
export interface RecognizedTarget {
  projectId: string
  sectionId: string | null
  lifeSpace: LifeSpace
  projectName: string
  sectionTitle: string | null
}

export interface TitleRecognition {
  parsed: ParsedTitle
  /** Erkanntes Projekt (oder ein Abschnitt des Seitenprojekts); `null` ohne. */
  target: RecognizedTarget | null
  /** Was es gibt — aktive Projekte, geladene Abschnitte, Tags; die Vorschläge (Schritt C) lesen daraus. */
  catalog: TitleCatalog
  /** „Als Text lassen“ — die Stelle setzt nichts mehr. */
  dismiss: (tokens: TitleToken | TitleToken[]) => void
  /** Ein als Text gelassenes Wort doch erkennen. */
  activate: (token: TitleToken) => void
  /**
   * Im Vorschlag gewählt (Schritt C): `nextText` ist der Titel nach dem Übernehmen, `start`/`end` die Stelle
   * darin. Die Wahl wandert mit dem Tippen mit und schlägt alle Regeln für gleichnamige Projekte.
   */
  choose: (nextText: string, choice: Choice) => void
  /** Ein eben angelegter Abschnitt gehört sofort zum Katalog — sonst bliebe `/Todo` bis zum nächsten Laden Text. */
  addSection: (section: Section) => void
  /** Der erkannte Teil, in dem der Caret steht (nicht an seinen Rändern). */
  tokenInside: (position: number) => TitleToken | undefined
  /** Ein als Text gelassener, erkennbarer Teil an dieser Stelle. */
  suppressedInside: (position: number) => TitleToken | undefined
}

export interface Choice extends TextRange {
  kind: 'project' | 'section'
  id: string
}

interface RangeState {
  text: string
  /** Im Vorschlag gewählt — die Stelle samt Projekt bzw. Abschnitt. */
  chosen: Choice[]
  /** Von Hand als Text gelassen. */
  dismissed: TextRange[]
  /** Von Hand wieder markiert — schlägt den Schutz des gespeicherten Titels. */
  activated: TextRange[]
  /** Was vom gespeicherten Titel noch unberührt dasteht — Schritt für Schritt mitgeführt. */
  kept: KeptSegment[]
  /** Der gespeicherte Titel, zu dem `kept` gehört. */
  keptFor: string | null
}

const ranges = (tokens: TitleToken[]) => tokens.map(({ start, end }) => ({ start, end }))

/** Eine Wahl überlebt das Tippen, solange ihr Wort unberührt bleibt — wie die gelassenen Stellen. */
const remapChoices = (prev: string, next: string, choices: Choice[]): Choice[] =>
  choices.flatMap(choice => remapRanges(prev, next, [choice]).map(range => ({ ...choice, ...range })))

function remapState(state: RangeState, next: string): RangeState {
  return {
    text: next,
    dismissed: remapRanges(state.text, next, state.dismissed),
    activated: remapRanges(state.text, next, state.activated),
    chosen: remapChoices(state.text, next, state.chosen),
    kept: remapSegments(state.text, next, state.kept),
    keptFor: state.keptFor,
  }
}

/** Ganz frisch: Der gespeicherte Titel steht (fast immer) unberührt im Feld. */
function freshState(title: string, protectedText: string | null, reset: unknown): RangeState & { reset: unknown } {
  const kept = protectedText ? remapSegments(protectedText, title, [{ from: 0, start: 0, end: protectedText.length }]) : []
  return { text: title, dismissed: [], activated: [], chosen: [], kept, keptFor: protectedText, reset }
}

/**
 * **Die Erkennung im Titel, live** (#234, Schritte B1/B2). Parst bei jeder Änderung des Titels und hält
 * die als Text gelassenen Stellen fest — beim Tippen wandern sie mit (`remapRanges`).
 *
 * Abschnitte gibt es nur je Projekt. Gelesen werden sie einmalig je Projekt — für das der Seite und
 * das im Titel — und nur in der offenen Zeile: keine Abfrage je Listenzeile.
 */
export function useTitleRecognition({
  place, enabled, title, language, dateKey, tags, protectedText = null, resetSignal,
}: UseTitleRecognitionArgs): TitleRecognition {
  const source = useDataSource()
  const active = enabled && place != null
  const locale: 'de' | 'en' = language?.toLowerCase().startsWith('en') ? 'en' : 'de'

  // Die Stellen gehören zu genau einem Text. Ändert er sich, rücken sie gleich in diesem Render mit —
  // ein Effekt käme ein Bild zu spät, und das Wort blitzte markiert auf.
  const [state, setState] = useState(() => freshState(title, protectedText, resetSignal))
  let current = state
  if (state.reset !== resetSignal) {
    current = freshState(title, protectedText, resetSignal)
    setState(current)
  } else if (state.text !== title || state.keptFor !== protectedText) {
    const moved = remapState(state, title)
    // Ein neuer gespeicherter Titel (gespeichert, von anderswo geändert) gilt von hier an als unberührt.
    current = state.keptFor === protectedText
      ? { ...moved, reset: state.reset }
      : { ...freshState(title, protectedText, state.reset), dismissed: moved.dismissed, activated: moved.activated, chosen: moved.chosen }
    setState(current)
  }

  // Eine zugeklappte Zeile rechnet nichts — eine Liste hat Hunderte davon.
  const projects = active ? place.projects : null
  const baseCatalog = useMemo((): TitleCatalog => (projects ? {
    projects: projects
      .filter(p => !p.deletedAt && !p.isSystem)
      .sort((a, b) => a.order - b.order)
      .map(p => ({ id: p.id, name: p.name, lifeSpace: p.lifeSpace, status: p.status })),
    sections: [],
    tags: tags.filter(tag => !tag.deletedAt).map(tag => ({ id: tag.id, name: tag.name })),
  } : NO_CATALOG), [projects, tags])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const now = useMemo(() => new Date(), [dateKey, title])

  const options = useMemo((): Omit<ParseTitleOptions, 'catalog' | 'dismissed'> | null => (active ? {
    locale, now, currentLifeSpace: place.lifeSpace, contextProjectId: place.projectId, preferredProjectId: place.preferredProjectId,
  } : null), [active, locale, now, place])

  // Welche Projekte Abschnitte brauchen: das der Seite, die in der Liste gewählten und die, die im Titel
  // (oder im gespeicherten) stehen.
  const chosenProjects = current.chosen.filter(c => c.kind === 'project').map(c => c.id).join(',')
  const neededProjects = useMemo(() => {
    if (!options) return [] as string[]
    const ids = new Set<string>(chosenProjects ? chosenProjects.split(',') : [])
    if (options.contextProjectId) ids.add(options.contextProjectId)
    for (const text of [title, protectedText]) {
      if (!text?.includes('#')) continue
      const id = parseTaskTitle(text, { ...options, catalog: baseCatalog }).fields.project?.id
      if (id) ids.add(id)
    }
    return [...ids]
  }, [options, title, protectedText, baseCatalog, chosenProjects])

  const [sectionCache, setSectionCache] = useState<Record<string, Section[]>>({})
  const missing = neededProjects.filter(id => !(id in sectionCache)).join(',')
  useEffect(() => {
    if (!missing) return
    let alive = true
    for (const id of missing.split(',')) {
      void source.getSectionsByProject(id).then(list => {
        if (alive) setSectionCache(prev => ({ ...prev, [id]: list.filter(s => !s.archivedAt && s.title.trim() !== '') }))
      })
    }
    return () => { alive = false }
  }, [missing, source])

  const catalog = useMemo((): TitleCatalog => ({
    ...baseCatalog,
    sections: neededProjects.flatMap(id => (sectionCache[id] ?? []).map(s => ({ id: s.id, projectId: s.projectId, title: s.title }))),
  }), [baseCatalog, neededProjects, sectionCache])

  // Was der gespeicherte Titel schon trug — jeder Treffer, nicht nur der erste je Art —, bleibt Text,
  // solange sein Wort unberührt dasteht.
  const storedRanges = useMemo(() => {
    if (!options || !protectedText?.trim()) return NO_RANGES
    return recognizableRanges(protectedText, { ...options, catalog })
  }, [options, protectedText, catalog])
  const kept = current.kept
  const protectedRanges = useMemo(
    () => (storedRanges.length && protectedText ? carryRanges(protectedText, title, kept, storedRanges) : NO_RANGES),
    [storedRanges, protectedText, title, kept],
  )

  const dismissed = useMemo(
    () => [...protectedRanges, ...current.dismissed].filter(r => !current.activated.some(a => overlaps(a, r))),
    [protectedRanges, current.dismissed, current.activated],
  )

  const chosen = current.chosen
  const parsed = useMemo(() => {
    if (!options || !title.trim()) return EMPTY
    return parseTaskTitle(title, { ...options, catalog, dismissed, chosen })
  }, [options, title, catalog, dismissed, chosen])

  const target = useMemo((): RecognizedTarget | null => {
    const { project, section } = parsed.fields
    const projectId = project?.id ?? (section ? place?.projectId : null)
    if (!projectId) return null
    const catalogProject = baseCatalog.projects.find(p => p.id === projectId)
    return {
      projectId,
      sectionId: section?.id ?? null,
      lifeSpace: project?.lifeSpace ?? catalogProject?.lifeSpace ?? place!.lifeSpace,
      projectName: catalogProject?.name ?? '',
      sectionTitle: section ? catalog.sections.find(s => s.id === section.id)?.title ?? null : null,
    }
  }, [parsed.fields, place, baseCatalog.projects, catalog.sections])

  const dismiss = useCallback((tokens: TitleToken | TitleToken[]) => {
    const list = Array.isArray(tokens) ? tokens : [tokens]
    setState(prev => ({
      ...prev,
      dismissed: [...prev.dismissed, ...ranges(list)],
      activated: prev.activated.filter(a => !list.some(token => overlaps(a, token))),
    }))
  }, [])

  const activate = useCallback((token: TitleToken) => {
    setState(prev => ({
      ...prev,
      dismissed: prev.dismissed.filter(r => !overlaps(r, token)),
      activated: [...prev.activated, { start: token.start, end: token.end }],
    }))
  }, [])

  const choose = useCallback((nextText: string, choice: Choice) => {
    setState(prev => {
      const next = remapState(prev, nextText)
      return {
        ...next,
        reset: prev.reset,
        // Die Wahl ersetzt, was an dieser Stelle galt — auch ein „als Text gelassen“.
        dismissed: next.dismissed.filter(r => !overlaps(r, choice)),
        chosen: [...next.chosen.filter(c => !overlaps(c, choice)), choice],
      }
    })
  }, [])

  const addSection = useCallback((section: Section) => {
    setSectionCache(prev => ({ ...prev, [section.projectId]: [...(prev[section.projectId] ?? []), section] }))
  }, [])

  const tokenInside = useCallback(
    (position: number) => parsed.tokens.find(token => token.start < position && position < token.end),
    [parsed.tokens],
  )
  const suppressedInside = useCallback(
    (position: number) => parsed.suppressed.find(token => token.start < position && position < token.end),
    [parsed.suppressed],
  )

  return { parsed, target, catalog, dismiss, activate, choose, addSection, tokenInside, suppressedInside }
}
